import { Disclosure } from '@headlessui/react'
import { observer } from 'mobx-react-lite'
import { ReactElement, useMemo } from 'react'
import { TypedReadOnlyV2, TypedInputV2, TypedComboV2, TypedAsyncComboV2 } from '../TypedFormFieldV2'
import { ChevronToggle } from 'app/components/ChevronToggle'
import { LeadStatusTrigger } from 'app/components/contacts/LeadStatusTrigger'
import { AIBadgeIcon } from 'app/components/Icon/AIBadgeIcon'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import type { ContactJson } from 'app/types/entities'
import { hasRole, useUser } from 'app/utils'
import { getDefaultSpotlightOrder, SectionItem } from 'lib/contactDrawerConfig'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { getFieldConfig, getFieldValue } from 'lib/contactFieldConfig'
import { isSMCOrg } from 'lib/isSMCOrg'

type Props = {
  contact: ContactJson
  config?: SectionItem
  isAgentOnly?: boolean
  isAIThread?: boolean
}

/**
 * ContactSpotlight component displays key contact information in a collapsible section.
 * Open by default to show the most important contact fields at a glance.
 */

// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
export const ContactSpotlight = observer(function ContactSpotlight({
  contact,
  config,
  isAgentOnly = false,
  isAIThread,
}: Props): ReactElement {
  const user = useUser()
  const checkPermission = useCheckPermission()
  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const staffDashboardV2 = useFeatureToggle(`staff-dashboard-v2`)
  const isIntegrated = useFeatureToggle(`integrated-mode`)
  const canCustomizeDrawer = useFeatureToggle(`contact-drawer-customization`)

  // Get field order from config or fall back to default organization-specific order
  const fieldOrder = useMemo(() => {
    if (config?.children && canCustomizeDrawer) {
      return config.children.filter((child) => child.visible).map((child) => child.id)
    }

    // Fall back to default organization-specific order
    return getDefaultSpotlightOrder(contact.orgId)
  }, [config, canCustomizeDrawer, contact.orgId])

  if (!user) return <></>

  // Legacy: all workspace users could edit these fields
  const legacyCanEdit = user.type === `super` || hasRole(user, `workspaceUser`)

  return (
    <div className="flex rounded-md bg-surface p-4">
      <Disclosure defaultOpen>
        {({ open }) => (
          <div className="flex w-full flex-col gap-y-4">
            <Disclosure.Button className="min-h-6 flex w-full items-center gap-x-2">
              <div className="text-heading-xs text-strong">Spotlight</div>
              <ChevronToggle open={open} />
            </Disclosure.Button>
            <Disclosure.Panel>
              <div className="grid grid-cols-1">
                {fieldOrder.map((fieldId) => {
                  const fieldConfig = getFieldConfig(fieldId)
                  if (!fieldConfig) return null

                  // Check conditional rendering rules
                  if (fieldConfig.conditionalRender) {
                    const shouldRender = fieldConfig.conditionalRender({
                      fieldId,
                      contact,
                      user,
                      isIntegrated,
                    })
                    if (!shouldRender) return null
                  }

                  // Get field value using centralized getValue function
                  const fieldValue = getFieldValue(fieldId, contact)
                  // Render appropriate component based on field configuration
                  const renderField = () => {
                    const commonProps = {
                      field: fieldId as keyof ContactJson,
                      label: fieldConfig.label,
                      value: fieldValue,
                      emptyMessage: fieldValue ? undefined : `—`,
                      className: fieldValue ? fieldConfig.className : undefined,
                      isSpotlight: true,
                    }

                    const readOnlyProps = {
                      ...commonProps,
                      isPhone: fieldConfig.isPhone,
                      canCopy: fieldConfig.canCopy,
                      isDate: fieldConfig.isDate,
                    }

                    const renderReadOnly = (overrides: Partial<typeof readOnlyProps> = {}) => (
                      <TypedReadOnlyV2<ContactJson> {...readOnlyProps} {...overrides} />
                    )

                    // For Agent Only mode: force Lead Status and Contact Owner to show placeholders
                    // and make all fields read-only
                    if (isAgentOnly) {
                      if (fieldId === `leadStatus`) {
                        return renderReadOnly({ field: `leadStatus`, value: ``, emptyMessage: `Set Lead Status` })
                      }
                      if (fieldId === `leadOwner`) {
                        return renderReadOnly({ field: `leadOwner`, value: ``, emptyMessage: `Set Owner` })
                      }
                      return renderReadOnly()
                    }

                    // If field is not editable, always render as read-only
                    if (!fieldConfig.isEditable) {
                      return renderReadOnly()
                    }

                    // For editable fields, determine component type
                    switch (fieldId) {
                      case `leadStatus`: {
                        // If Closed Won, render as read-only
                        if (contact.leadStatus === `Closed Won`) {
                          return renderReadOnly()
                        }
                        // Check permission for editing lead status
                        // Note: Lead status can still be changed via follow-ups/click-to-call even without this permission
                        // staff-v2 ON: check role permissions, staff-v2 OFF: legacy (all workspace users)
                        const hasLeadStatusPermission = staffV2Enabled
                          ? checkPermission([`org:contacts:edit-lead-status`, `contacts:lead-status`])
                          : legacyCanEdit
                        if (!hasLeadStatusPermission) {
                          return renderReadOnly()
                        }
                        return (
                          <TypedComboV2<ContactJson>
                            {...commonProps}
                            renderTrigger={(status) => (
                              <LeadStatusTrigger
                                status={status as ContactJson[`leadStatus`]}
                                data-testid="lead-status-field"
                              />
                            )}
                            options={[
                              { id: `Needs Action`, name: `Needs Action`, value: `Needs Action` },
                              { id: `In Progress`, name: `In Progress`, value: `In Progress` },
                              { id: `Scheduled`, name: `Scheduled`, value: `Scheduled` },
                              { id: `Closed Lost`, name: `Closed Lost`, value: `Closed Lost` },
                            ]}
                          />
                        )
                      }

                      case `leadOwner`:
                        return (
                          <TypedAsyncComboV2<ContactJson>
                            {...commonProps}
                            fetcher={async (params) => {
                              const result = await gqlFetchList<{ id: string; name: string }>(
                                `staffMembers`,
                                { search: params.query || undefined, pagination: { limit: 20 } },
                                `id name`,
                              )
                              return result
                            }}
                            optionsMapper={(res) =>
                              res?.hits?.map((hit: { id: string; name: string }) => ({
                                id: hit.id || hit.name,
                                name: hit.name,
                                value: hit.name,
                              })) || []
                            }
                          />
                        )

                      case `isQualified`: {
                        // SMC: hide qualification on Closed Lost
                        if (isSMCOrg(contact.orgId) && contact.leadStatus === `Closed Lost`) {
                          return null
                        }
                        // Check permission for editing qualification
                        // staff-v2 ON: check role permissions, staff-v2 OFF: legacy (all workspace users)
                        const hasQualificationPermission = staffV2Enabled
                          ? checkPermission([`org:contacts:edit-qualification`, `contacts:qualifications`])
                          : legacyCanEdit
                        if (hasQualificationPermission) {
                          const isReadOnly =
                            contact.patientStatus === `NonPatient` ||
                            [`New Patient Scheduled`, `Patient`].includes(contact.lifecycleStage!)
                          if (isReadOnly) {
                            return renderReadOnly()
                          }
                          return (
                            <TypedComboV2<ContactJson>
                              {...commonProps}
                              field="isQualified"
                              value={String(contact.isQualified)}
                              options={[
                                { id: `true`, name: `Qualified`, value: `true` },
                                { id: `false`, name: `Not Qualified`, value: `false` },
                                { id: `undefined`, name: `Not Set`, value: `undefined` },
                              ]}
                            />
                          )
                        }
                        // Fall back to read-only if no permission
                        return renderReadOnly()
                      }

                      case `channel`: {
                        // SMC orgs: editable if user has permission, otherwise read-only
                        // Non-SMC orgs: always read-only
                        if (contact.orgId && isSMCOrg(contact.orgId)) {
                          // staff-v2 ON: check role permissions, staff-v2 OFF: legacy (all workspace users)
                          const hasChannelPermission = staffV2Enabled
                            ? checkPermission([`org:contacts:edit-channel`, `contacts:attributions`])
                            : legacyCanEdit
                          if (hasChannelPermission) {
                            return (
                              <TypedComboV2<ContactJson>
                                {...commonProps}
                                options={[
                                  { id: `Digital Marketing`, name: `Digital Marketing`, value: `Digital Marketing` },
                                  {
                                    id: `Traditional Marketing`,
                                    name: `Traditional Marketing`,
                                    value: `Traditional Marketing`,
                                  },
                                  { id: `Referral Marketing`, name: `Referral Marketing`, value: `Referral Marketing` },
                                ]}
                              />
                            )
                          }
                        }
                        // Non-SMC orgs or SMC without permission: read-only
                        return renderReadOnly()
                      }

                      case `source`:
                      case `medium`:
                      case `campaign`:
                        return (
                          <TypedInputV2<ContactJson>
                            {...commonProps}
                            field={fieldConfig.editFieldName as keyof ContactJson}
                          />
                        )

                      default:
                        // For any other editable field, use input component if it has editFieldName
                        if (fieldConfig.isEditable && fieldConfig.editFieldName) {
                          return (
                            <TypedInputV2<ContactJson>
                              {...commonProps}
                              field={fieldConfig.editFieldName as keyof ContactJson}
                            />
                          )
                        }
                        // Otherwise render as read-only
                        return renderReadOnly()
                    }
                  }

                  return (
                    <div key={fieldId}>
                      {fieldId === `cellPhone` && isAIThread ? (
                        <div className="flex items-center gap-x-1">
                          {renderField()}
                          <AIBadgeIcon className="mb-1 h-3.5 w-3.5 shrink-0 opacity-80" />
                        </div>
                      ) : (
                        renderField()
                      )}
                    </div>
                  )
                })}
              </div>
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>
    </div>
  )
})
